import React, { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import {
  PhoneIncoming,
  PhoneOutgoing,
  PhoneMissed,
  Voicemail,
  Loader2,
  Phone,
  Clock,
} from "lucide-react";
import { callLogsApi } from "../../services/api";

interface CallsTabProps {
  onViewDetails: (callLog: any) => void;
}

type FilterTab = "all" | "missed" | "voicemail";

function formatSmartTimestamp(dateStr: string): string {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today.getTime() - 86400000);
  const callDay = new Date(d.getFullYear(), d.getMonth(), d.getDate());

  if (callDay.getTime() === today.getTime()) {
    return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  }
  if (callDay.getTime() === yesterday.getTime()) {
    return "Yesterday";
  }
  return d.toLocaleDateString([], {
    month: "short",
    day: "numeric",
    year:
      d.getFullYear() !== now.getFullYear() ? "numeric" : undefined,
  });
}

function formatCallDuration(secs: number): string {
  if (!secs || secs < 1) return "";
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  if (m === 0) return `${s}s`;
  return `${m}m ${s}s`;
}

const statusBadgeStyles: Record<string, { bg: string; text: string }> = {
  completed: { bg: "bg-emerald-100", text: "text-emerald-700" },
  missed: { bg: "bg-red-100", text: "text-red-700" },
  no_answer: { bg: "bg-red-100", text: "text-red-700" },
  voicemail: { bg: "bg-amber-100", text: "text-amber-700" },
  failed: { bg: "bg-red-100", text: "text-red-700" },
  initiated: { bg: "bg-blue-100", text: "text-blue-700" },
  ringing: { bg: "bg-blue-100", text: "text-blue-700" },
  in_progress: { bg: "bg-blue-100", text: "text-blue-700" },
};

function getDirectionIcon(callLog: any) {
  if (callLog.status === "missed" || callLog.status === "no_answer") {
    return <PhoneMissed className="h-4 w-4 text-red-500" />;
  }
  if (callLog.status === "voicemail") {
    return <Voicemail className="h-4 w-4 text-amber-600" />;
  }
  if (callLog.direction === "inbound") {
    return <PhoneIncoming className="h-4 w-4 text-blue-600" />;
  }
  return <PhoneOutgoing className="h-4 w-4 text-emerald-600" />;
}

function getContactInfo(callLog: any): { name: string; phone: string } {
  if (callLog.direction === "outbound") {
    return {
      name: callLog.to?.displayName || "",
      phone: callLog.to?.phoneE164 || "Unknown",
    };
  }
  return {
    name: callLog.from?.displayName || "",
    phone: callLog.from?.phoneE164 || "Unknown",
  };
}

const filterTabs: { key: FilterTab; label: string }[] = [
  { key: "all", label: "All" },
  { key: "missed", label: "Missed" },
  { key: "voicemail", label: "Voicemail" },
];

const CallsTab: React.FC<CallsTabProps> = ({ onViewDetails }) => {
  const [filter, setFilter] = useState<FilterTab>("all");
  const [callLogs, setCallLogs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchCalls = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, any> = {};
      if (filter === "missed") {
        params.status = "missed,no_answer";
      } else if (filter === "voicemail") {
        params.status = "voicemail";
      }
      const res = await callLogsApi.list(params);
      setCallLogs(res.data?.data || res.data || []);
    } catch (err) {
      console.error("[CallsTab] Failed to fetch call logs:", err);
      setCallLogs([]);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    fetchCalls();
  }, [fetchCalls]);

  return (
    <div className="flex flex-col h-full">
      {/* Filter pills */}
      <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-100">
        {filterTabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setFilter(tab.key)}
            className={`px-4 py-1.5 text-xs font-semibold rounded-full transition-colors ${
              filter === tab.key
                ? "bg-brand text-white"
                : "bg-slate-100 text-slate-600 hover:bg-slate-200"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-6 w-6 text-slate-400 animate-spin" />
          </div>
        ) : callLogs.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center px-4">
            <div className="h-12 w-12 rounded-full bg-slate-100 flex items-center justify-center mb-3">
              <Phone className="h-6 w-6 text-slate-400" />
            </div>
            <p className="text-sm font-medium text-slate-500">No calls yet</p>
            <p className="text-xs text-slate-400 mt-1">
              {filter === "missed"
                ? "No missed calls to show."
                : filter === "voicemail"
                  ? "No voicemails to show."
                  : "Your call history will appear here."}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {callLogs.map((callLog, index) => {
              const contact = getContactInfo(callLog);
              const badge = statusBadgeStyles[callLog.status] || {
                bg: "bg-slate-100",
                text: "text-slate-700",
              };

              return (
                <motion.div
                  key={callLog._id || index}
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.15, delay: index * 0.03 }}
                  onClick={() => onViewDetails(callLog)}
                  className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-slate-50 transition-colors"
                >
                  {/* Direction icon */}
                  <div className="h-9 w-9 rounded-full bg-slate-50 flex items-center justify-center flex-shrink-0">
                    {getDirectionIcon(callLog)}
                  </div>

                  {/* Contact info */}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-slate-800 truncate">
                      {contact.name || contact.phone}
                    </p>
                    {contact.name && (
                      <p className="text-xs text-slate-500 truncate">
                        {contact.phone}
                      </p>
                    )}
                  </div>

                  {/* Status badge */}
                  <span
                    className={`px-2.5 py-0.5 text-xs font-semibold rounded-full flex-shrink-0 ${badge.bg} ${badge.text}`}
                  >
                    {callLog.status?.replace("_", " ").replace(/\b\w/g, (c: string) =>
                      c.toUpperCase()
                    )}
                  </span>

                  {/* Duration */}
                  {callLog.status === "completed" && callLog.durationSecs ? (
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <Clock className="h-3 w-3 text-slate-400" />
                      <span className="text-xs text-slate-500">
                        {formatCallDuration(callLog.durationSecs)}
                      </span>
                    </div>
                  ) : null}

                  {/* Timestamp */}
                  <span className="text-xs text-slate-400 flex-shrink-0 min-w-[60px] text-right">
                    {formatSmartTimestamp(
                      callLog.startedAt || callLog.createdAt
                    )}
                  </span>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default CallsTab;
